<script setup lang="ts">
import { useComparatorStore } from "@modules/courtage/stores/comparator.store"
import {
    ComparatorOutput,
    ComparatorOutputWarning,
    ComparatorResultOfferItemCellValues,
    MeanValue,
    NoStatValue,
    NotificationEvent,
    NotificationEventTask,
    SumMeanValue,
    SumValue,
} from "@types"
import { useEventBus } from "@vueuse/core"
import moment from "moment"
import { storeToRefs } from "pinia"
import { ToastMessageOptions, useConfirm, useToast } from "primevue"
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"

const comparatorStore = useComparatorStore()

const FORBIDDEN_CODES_WARNINGS = [
    "oc-pas-pp1", // Avertissement déjà affiché dans le ComparatorResultModal
]

const { isFetchingCompareOffersAnalysis, warnings } =
    storeToRefs(comparatorStore)

type Props = {
    name: string
    createdAt: Date
    output: ComparatorOutput
    disabledSave: boolean
    disabledExport: boolean
    disabledEditName?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    name: "",
    createdAt: () => new Date(),
    disabledSave: false,
    disabledExport: false,
})

const { t, te } = useI18n({
    messages: {
        fr: {
            name: ({ named }: { named: (key: string) => string }) =>
                named("name") ? named("name") : "Comparaison des offres",
            createdAt: ({ named }: { named: (key: string) => string }) =>
                `Réalisé le ${named("createdAt")}`,
            save: "Sauvegarder",
            export: "Exporter",
            sort: "Trier",

            warnings: {
                "data-rescaled":
                    "Attention, ces coûts de capacité ont été calculés sur des consommations par poste éditées et non remontées directement via Enedis.",
            },
            download: {
                message:
                    "Le résultat du comparateur sera sauvegardé avant le téléchargement.",
                accept: "Sauvegarder et télécharger",
                reject: "Annuler",
            },
        },
    },
})

const modelName = ref(props.name)
const snapshotName = ref(props.name)
const attachedOffer = ref(true)

const variablesToShow = [
    "prixHTT_elecsoutirage_abonnement.sum",
    "prixHTT_elecsoutirage_consommation.sum",
    "prixHTT_elecsoutirage_turpe.sum",
    "prixHTT_elecsoutirage_cee.sum",
    "prixHTT_elecsoutirage_oc.sum",
    "prixHTVA_elecsoutirage_tout.sum",
    "prixTTC_elecsoutirage_tout.sum",
    "prixHTVAparkWh_elecsoutirage_tout.mean",
    "vaprixTTC_elecsoutirage_tout.mean",
]

function formatRow(
    name: string,
    data: Record<string, SumValue | MeanValue | SumMeanValue | NoStatValue>,
    frozen: boolean = false,
    variables: string[] = variablesToShow
) {
    return {
        name,
        data: Object.entries(data)
            .filter(([variable]) => {
                return variables.map((v) => v.split(".")[0]).includes(variable)
            })
            .reduce((acc, [variable, data]) => {
                const stat = variables
                    .find((v) => v.split(".")[0] === variable)
                    ?.split(".")[1]

                acc[variable as keyof ComparatorResultOfferItemCellValues] =
                    data[stat as keyof typeof data]
                return acc
            }, {} as ComparatorResultOfferItemCellValues),
        frozen,
    }
}

const getOffers = computed(() => {
    const rowActuelle = formatRow(
        "Période de conso. de réf.",
        props.output.offre_actuelle.data.periode,
        false
    )

    const plageRows = props.output.offre_actuelle.data.plage.distributeur.map(
        (plage, index) => {
            return {
                ...formatRow(
                    plage.plage_elecsoutirage_distributeur.nostat,
                    plage,
                    false
                ),
                id: `offre_actuelle-plage_${index}`,
            }
        }
    )

    // Recherche l'offre avec le plus haut ranking
    const rakingOffers = props.output.offres_comparees.map((offre) => ({
        idoffre: offre.idoffre,
        ranking: offre.ranking,
    }))
    const optimalOffer = rakingOffers.reduce((min, offre) => {
        return offre.ranking < min.ranking ? offre : min
    }, props.output.offres_comparees[0])

    const offre_actuelle = {
        actual: true,
        index: 0,
        rows: [
            {
                ...rowActuelle,
                id: "offre_actuele",
                plages: plageRows,
            },
        ],
        nomOffre: props.output.offre_actuelle.nom_offre,
        idoffre: props.output.offre_actuelle.idoffre,
        supplier: props.output.offre_actuelle._id_supplier,
        attached: attachedOffer.value,
        optimal: optimalOffer.idoffre === props.output.offre_actuelle.idoffre,
        // Ajoute le ranking de l'offre actuelle (de référence) en parcourant les offres comparées
        ranking:
            rakingOffers.find(
                (offre) => offre.idoffre === props.output.offre_actuelle.idoffre
            )?.ranking ?? -1,
    }

    const offres_comparees = props.output.offres_comparees
        .filter(
            (offre) => offre.idoffre !== props.output.offre_actuelle.idoffre
        )
        .map((offre, index) => {
            // Une ligne représente une période (ex: 2025, 2024 etc)
            const rows = offre.periodes.map((periode, index, array) => {
                const name = periode.idperiode
                // Ajoute un suffixe alphabétique si plusieurs périodes ont le même nom
                const duplicateCount = array.filter(
                    (p) => p.idperiode === name
                ).length
                // Index de la période parmi les périodes du même nom
                const duplicateIndex =
                    array
                        // Ne garde que les périodes jusqu'à l'index courant
                        .slice(0, index + 1)
                        .filter((p) => p.idperiode === name).length - 1

                if (duplicateCount > 1) {
                    return {
                        ...formatRow(
                            `${name}.${String.fromCharCode(65 + duplicateIndex)}`,
                            periode.data.periode,
                            false
                        ),
                        id: `offre_comparee_${index}`,
                    }
                }

                // Récupère les données par plages de distributeur
                const plageRows = periode.data.plage.distributeur.map(
                    (plage, index) => {
                        return {
                            ...formatRow(
                                plage.plage_elecsoutirage_distributeur.nostat,
                                plage,
                                false
                            ),
                            id: `offre_comparee_${index}-plage-${index}`,
                        }
                    }
                )

                return {
                    ...formatRow(
                        periode.idperiode,
                        periode.data.periode,
                        false
                    ),
                    id: `offre_comparee_${index}`,
                    plages: plageRows,
                }
            })

            // Ajoute les lignes total et moyenne annuelle si on a plus de 1 période
            const frozonRows =
                rows.length > 1
                    ? [
                          formatRow("Total", offre.data.periodes, true),
                          formatRow(
                              "Moy. annuelle",
                              offre.data.periodes,
                              true,
                              [
                                  "prixHTT_elecsoutirage_abonnement.mean",
                                  "prixHTT_elecsoutirage_consommation.mean",
                                  "prixHTT_elecsoutirage_turpe.mean",
                                  "prixHTT_elecsoutirage_cee.mean",
                                  "prixHTT_elecsoutirage_oc.mean",
                                  "prixHTVA_elecsoutirage_tout.mean",
                                  "prixTTC_elecsoutirage_tout.mean",
                                  "vaprixTTC_elecsoutirage_tout.mean",
                              ]
                          ),
                      ]
                    : []

            return {
                actual: false,
                index: index + 1,
                nomOffre: offre.nom_offre,
                idoffre: offre.idoffre,
                optimal: offre.idoffre === optimalOffer.idoffre,
                rows: [...rows, ...frozonRows],
                ranking: offre.ranking,
                supplier: offre._id_supplier,
            }
        })

    return [offre_actuelle, ...offres_comparees]
})

const getSortedOffers = computed(() => {
    return (
        [...getOffers.value]
            // Trie les offres par ranking du plus petit au plus grand
            .sort((a, b) => (a.ranking ? a.ranking - b.ranking : 1))
            // Si on a l'offre actuelle (de référence) qui est mis en avant avec attached = true, on la met en premier
            .sort((a) => (attachedOffer.value ? (a.actual ? -1 : 1) : 1))
    )
})

const bus = useEventBus<NotificationEvent>("notification")
async function onClickSaveAnalysis() {
    // On sauvegarde l'analyse
    await comparatorStore.createCompareOffersAnalysis({
        name: modelName.value,
    })

    if (!comparatorStore.hasErrorCompareOffersAnalysis) {
        bus.emit({
            severity: "success",
            title: "Analyse sauvegardée",
            message: "L'analyse a été sauvegardée avec succès",
        })
    }
}

/**
 * Regroupe les warnings par code pour ne pas afficher plusieurs fois le même warning
 */
const getWarningsByCode = computed(() => {
    const uniqueWarnings: Record<string, ComparatorOutputWarning> = {}
    warnings.value
        .filter(({ code }) => {
            return !FORBIDDEN_CODES_WARNINGS.includes(code)
        })
        .forEach((warning) => {
            if (!uniqueWarnings[warning.code]) {
                // Si un message avec le même code existe dans la traduction alors on utilise celui-ci
                const translatedMessage = te(`warnings.${warning.code}`)
                if (translatedMessage) {
                    uniqueWarnings[warning.code] = {
                        ...warning,
                        message: t(`warnings.${warning.code}`),
                    }
                } else {
                    uniqueWarnings[warning.code] = warning
                }
            }
        })
    return Object.values(uniqueWarnings)
})

const confirm = useConfirm()
const busTask = useEventBus<NotificationEventTask>("task")
const busNotification = useEventBus<NotificationEvent>("notification")

const toast = useToast()

const confirmDownloadRapport = () => {
    confirm.require({
        header: t("download.message"),
        group: "download-rapport-card",
        acceptProps: {
            label: t("download.accept"),
            icon: "pi pi-download",
            severity: "primary",
        },
        rejectProps: {
            label: t("download.reject"),
            outlined: true,
            severity: "secondary",
        },
        accept: async () => {
            await downloadAnalysis()
        },
        reject: () => {},
    })
}

/**
 * Télécharge l'analyse
 * @param analysisId - Identifiant de l'analyse
 */
async function downloadAnalysis() {
    const promise = new Promise<void>((resolve, reject) => {
        // On sauvegarde l'analyse
        comparatorStore.createCompareOffersAnalysis({
            name: modelName.value,
            callback: async (analysisId: string) => {
                // On télécharge le rapport de l'analyse
                await comparatorStore.fetchCompareOffersExportByAnalyseId({
                    analysisId,
                    callback: (url: string) => {
                        if (!url) {
                            // On affiche une notification d'erreur
                            bus.emit({
                                severity: "error",
                                title: "Erreur",
                                message:
                                    "Le lien de téléchargement n'a pas été trouvé",
                            })
                        } else {
                            window.open(url, "_blank")
                        }
                    },
                })
                if (comparatorStore.hasErrorAnalysisExport) {
                    reject()
                } else {
                    resolve()
                }
            },
        })
    })

    let notification: ToastMessageOptions | null = null

    function callback(_notification: ToastMessageOptions) {
        notification = _notification
    }
    // Affiche la notification de tâche en chargement
    busTask.emit({
        severity: "info",
        title: "Téléchargement en cours",
        message: "Le rapport est en cours de création, veuillez patienter...",
        callback,
    })
    if (promise) {
        try {
            await promise
        } catch {
            busNotification.emit({
                severity: "error",
                title: "Erreur",
                message: "Le téléchargement du rapport a échoué",
            })
        } finally {
            if (notification) toast.remove(notification)
        }
    }
}
</script>

<template>
    <div class="comparator-result-card card p-6 bg-white flex flex-col gap-6">
        <!-- HEADER -->
        <div class="flex items-center justify-between">
            <!-- SECTION GAUCHE - NOM -->
            <div class="flex items-center gap-2">
                <h2
                    v-if="disabledEditName"
                    class="font-bold cursor-default! text-primary-700"
                    @click.prevent.stop
                >
                    {{ t("name", { name: modelName }) }}
                </h2>
                <BaseInplace v-else>
                    <template #display>
                        <div class="flex items-center gap-2">
                            <h2
                                class="font-bold cursor-default! text-primary-700"
                                @click.prevent.stop
                            >
                                {{ t("name", { name: modelName }) }}
                            </h2>
                            <BaseButton
                                link
                                icon="pi pi-pencil"
                                @click="snapshotName = modelName"
                            ></BaseButton>
                        </div>
                    </template>
                    <template #content="{ closeCallback }">
                        <span class="inline-flex items-center gap-2">
                            <BaseInputText v-model="snapshotName" autofocus />
                            <BaseButton
                                icon="pi pi-check"
                                text
                                severity="success"
                                @click="
                                    () => {
                                        modelName = snapshotName
                                        closeCallback()
                                    }
                                "
                            />
                            <BaseButton
                                icon="pi pi-times"
                                text
                                severity="danger"
                                @click="
                                    () => {
                                        snapshotName = modelName
                                        closeCallback()
                                    }
                                "
                            />
                        </span>
                    </template>
                </BaseInplace>
                <!-- Séparateur -->

                <p class="text-sm flex items-center gap-4 text-primary-700">
                    <span class="text-sm">•</span>
                    {{
                        t("createdAt", {
                            createdAt: moment(createdAt).format("DD/MM/YYYY"),
                        })
                    }}
                </p>
            </div>
            <!-- SECTION DROITE - ACTIONS -->
            <div class="flex items-center gap-2">
                <!-- <BaseButton icon="pi pi-sort-alt" :label="t('sort')" text /> -->
                <BaseButton
                    v-if="!disabledSave"
                    icon="pi pi-save"
                    :label="t('save')"
                    outlined
                    :loading="isFetchingCompareOffersAnalysis"
                    @click="onClickSaveAnalysis"
                />
                <BaseButton
                    v-if="!disabledExport"
                    icon="pi pi-download"
                    :label="t('export')"
                    @click="confirmDownloadRapport()"
                />
            </div>
        </div>
        <transition-group
            name="list"
            tag="ul"
            class="flex flex-col gap-6"
            appear
        >
            <li v-for="offer in getSortedOffers" :key="offer.idoffre">
                <ComparatorResultOfferItem
                    :key="offer.idoffre"
                    v-bind="offer"
                    @toggle:attached="attachedOffer = !attachedOffer"
                />
            </li>
        </transition-group>
        <div v-if="warnings.length > 0">
            <BaseMessage
                v-for="warning in getWarningsByCode"
                :key="warning.code"
                severity="warn"
                class="mb-4"
            >
                <p class="md:text-sm text-xs">
                    {{ warning.message }}
                </p>
            </BaseMessage>
        </div>
        <BaseConfirmDialog
            :closable="false"
            position="top"
            group="download-rapport-card"
            :style="{ maxWidth: '35rem', width: '100%' }"
            :pt="{
                footer: 'justify-center!',
                header: 'justify-center!',
            }"
        ></BaseConfirmDialog>
    </div>
</template>

<style scoped>
.list-enter-active,
.list-leave-active,
.list-move {
    transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
}
.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateY(20px);
}
.list-leave-active {
    position: absolute;
}
</style>
